import PathwayLessonVm, {
  CourseLessonVm,
  LessonType
} from '../models/LessonVm';
import { escapeRegExp, isEmpty, isNil, isNumber } from 'lodash';

import ActionText from '../models/ActionText';
import CertStatus from '../models/CertStatus';
import { CompletionModel } from '../../shared/models/CompletionModel';
import { ContentType } from '../models/Pathway';
import { LessonAttempt } from '../models/LessonAttempt';
import { LessonCompletionType } from '../../content/models/LessonBehaviorSettings';
import { ModuleType } from '../models/ApiLesson';
import ProgressStatusCode from '../models/ProgressStatusCode';
import StatusText from '../models/StatusText';
import dayjs from 'dayjs';

export type CompletionStatus =
  | 'Completed'
  | 'NotCompleted'
  | 'Passed'
  | 'Failed';
class LessonService {
  public filterLessonsBy(
    pathwayLessons: PathwayLessonVm[] | CourseLessonVm[],
    contentType: ContentType,
    searchText: string,
    statusFilter: ProgressStatusCode[]
  ) {
    if (!searchText && isEmpty(statusFilter)) {
      return pathwayLessons;
    }

    const regexp = new RegExp(escapeRegExp(searchText), 'i');
    const filteredLessons = pathwayLessons.filter((l) => regexp.test(l.title));

    if (isEmpty(statusFilter)) {
      return filteredLessons;
    }
    if (contentType === 'pathway') {
      return filteredLessons.filter((lesson) =>
        (lesson as PathwayLessonVm).results?.some((r) =>
          statusFilter.includes(r.status)
        )
      );
    } else {
      return filteredLessons.filter((l) =>
        statusFilter.some(
          (s) =>
            // `Not started` lesson doesn't have `latestPortfolioAttempt` status.
            // So check with `moduleProgressStatus`.
            s ===
            ((l as CourseLessonVm).latestPortfolioAttempt?.status ||
              (l as CourseLessonVm).moduleProgressStatus)
        )
      );
    }
  }

  public toStatus(lesson: PathwayLessonVm): ProgressStatusCode {
    if (!lesson || isEmpty(lesson.results)) {
      return 'N';
    } else {
      return lesson.results[0].status;
    }
  }

  public toCertStatus(
    statusCode: ProgressStatusCode,
    cert: boolean
  ): CertStatus {
    if (!cert || statusCode === 'L') {
      return 'none';
    }
    switch (statusCode) {
      /** In order to display certificate icon color, lesson portfolio status
       * needs to be converted to 'CertStatus'.
       */
      case 'N': // Not Started
      case 'S': // Started, but not finished
      case 'F': // Failed for quiz, assessment
      case 'R': // Retry (Work Required) in assessment
      case 'M': // Submitted or being marked (Mark)
        return 'incomplete';
      case 'P': // Passed for quiz, assessment
      case 'C': // Completed for basic lesson
        return 'complete';
      case 'X': // Expiring
        return 'expiring';
      case 'E': // Expired
        return 'expired';
    }
  }

  public toActionText(statusCode: ProgressStatusCode): ActionText {
    if (statusCode === 'N') {
      return 'Start';
    } else if (statusCode === 'S') {
      return 'Resume';
    } else {
      return '';
    }
  }

  public toStatusText(
    statusCode: ProgressStatusCode,
    lessonType?: LessonType, //* Bracken 2 only
    completionType?: LessonCompletionType //* Bracken 2 only
  ): StatusText {
    switch (statusCode) {
      case 'L':
        return 'Locked';
      case 'N':
        return 'Not Started';
      case 'S':
        return 'Started';
      case 'M':
        return 'Submitted';
      case 'R':
        return 'Work Required';
      case 'P':
      case 'C':
        return (lessonType === 'quiz' && completionType === 'threshold') ||
          lessonType === 'assessment'
          ? 'Passed'
          : 'Completed';
      case 'X':
        return 'Expiring';
      case 'E':
        return 'Expired';
      case 'F':
        return 'Failed';
    }
  }

  public getStatusColor(status: ProgressStatusCode) {
    return (
      {
        L: '#30383E',
        N: '#616161',
        S: '#4D95E6',
        P: '#22AB64',
        C: '#30383E',
        X: '#F7AA18',
        E: '#F74B18',
        F: '#F74B18',
        R: '#F7AA2A',
        M: '#30383E'
      }[status] || '#30383e'
    );
  }

  public convertToLessonType(type: ModuleType): LessonType {
    switch (type) {
      case ModuleType.xAPI:
        return 'xapi';
      case ModuleType.Lesson:
        return 'lesson';
      case ModuleType.Quiz:
        return 'quiz';
      case ModuleType.Assessment:
        return 'assessment';
      case ModuleType.Critique:
        return 'critique';
      case ModuleType.Rubric:
        return 'rubric';
      default:
        return 'unknown';
    }
  }

  public getAttemptTitle(attempt: LessonAttempt) {
    if (isNil(attempt)) {
      return '';
    }
    if (attempt.created) {
      const timestamp = dayjs(attempt.created).format(
        'ddd, D MMM YYYY, hh:mm a'
      );
      return timestamp;
    }
  }

  public hasLessonCompleted(
    completionType: LessonCompletionType,
    completionThreshold: number,
    userTotalPoints: number,
    lessonTotalPoints: number
  ): boolean | null {
    if (isNil(completionType)) {
      return null;
    }

    return (
      completionType === 'atEnd' ||
      (completionType === 'threshold' &&
        isNumber(completionThreshold) &&
        isNumber(userTotalPoints) &&
        isNumber(lessonTotalPoints) &&
        userTotalPoints >= (lessonTotalPoints * completionThreshold) / 100)
    );
  }

  private getMessage(completionStatus: CompletionStatus): CompletionModel {
    switch (completionStatus) {
      case 'Completed':
        return {
          message: "You've completed this lesson.",
          color: '#30383E',
          icon: 'checkCircle'
        };
      case 'NotCompleted':
        return {
          message: "You haven't completed this lesson yet.",
          color: '#4D95E6',
          icon: 'error'
        };
      case 'Passed':
        return {
          message: "You've passed this quiz.",
          color: '#22AB64',
          icon: 'checkCircle'
        };
      case 'Failed':
        return {
          message: "You've failed this quiz.",
          color: '#DB624D',
          icon: 'cancel'
        };
    }
  }

  public getCompletionMessage(lessonType: LessonType, completed: boolean) {
    switch (lessonType) {
      case 'lesson':
        return completed
          ? this.getMessage('Completed')
          : this.getMessage('NotCompleted');
      case 'quiz':
        return completed
          ? this.getMessage('Passed')
          : this.getMessage('Failed');
    }
  }

  public getModuleEstimationTime(timeInSeconds: number): string {
    const timeString = ` | Time to complete:`;
    const totalMinutes = Math.max(Math.round(timeInSeconds / 60), 1); // Ensure at least 1 minute
    const hours = Math.floor(totalMinutes / 60); // Get the whole hours
    const minutes = totalMinutes % 60; // Get the remaining minutes
    const minuteString = minutes
      ? `${minutes} Min${minutes === 1 ? '' : 's'}`
      : '';
    if (hours) {
      return `${timeString} ${hours} Hr${
        hours === 1 ? '' : 's'
      } ${minuteString}`;
    } else {
      return `${timeString} ${minuteString}`;
    }
  }
}

export default new LessonService();
